import React from "react";
import { Stack, Typography } from "@mui/material";
import ActionButton from "@quest_editor/components/Buttons/ActionButton";
import BackButton from "@quest_editor/components/Buttons/BackButton";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "@quest_editor/components/Breadcrumb";
import PromotionTypeChip from "@quest_editor/components/Chips/PromotionTypeChip";
import PromotionStatusChip from "@quest_editor/components/Chips/PromotionStatusChip";
import { usePromotion, useDuplicatePromotion } from "@quest_editor/mocks";
import { getPromotionLabels } from "@quest_editor/utils/view/promotionLabels";
import { getBuildOptionsDisplay } from "@quest_editor/utils/view/buildOptionsDisplay";
import SourceInfoSection from "./SourceInfoSection";
import DestinationSettingsSection from "./DestinationSettingsSection";

const PromotionDuplication = () => {
  const { t } = useTranslation("questEditor");
  const navigate = useNavigate();
  const { id: promotionId } = useParams<{ id: string }>();

  const [newPromotionName, setNewPromotionName] = React.useState<string>("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { data: promotionData } = usePromotion(promotionId);
  const promotion = promotionData?.promotion;

  const { duplicatePromotion } = useDuplicatePromotion();
  const { getTypeLabel } = getPromotionLabels();
  const { buildOptionsDisplay } = getBuildOptionsDisplay();

  React.useEffect(() => {
    if (!promotion) return;
    setNewPromotionName(promotion.name);
  }, [promotion]);

  const handleDuplicate = async () => {
    if (!promotionId || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const result = await duplicatePromotion({ id: promotionId, newName: newPromotionName });
      const newPromotionId = result.data?.duplicatePromotion.id;
      if (newPromotionId) {
        navigate(`/quest_editor/promotion/${newPromotionId}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!promotion) {
    return <Typography>{t("common.label.not_found")}</Typography>;
  }

  return (
    <Stack direction="column" spacing={3} sx={{ flex: 1, minHeight: 0 }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Stack direction="column" spacing={2} alignItems="flex-start">
          <Breadcrumb items={[{ label: promotion.name }]} />
          <Stack direction="row" spacing={1}>
            <PromotionTypeChip type={promotion.type} />
            <PromotionStatusChip status={promotion.status} />
          </Stack>
        </Stack>
      </Stack>

      <SourceInfoSection
        promotion={promotion}
        getTypeLabel={getTypeLabel}
        buildOptionsDisplay={buildOptionsDisplay}
      />

      <DestinationSettingsSection
        newPromotionName={newPromotionName}
        setNewPromotionName={setNewPromotionName}
      />

      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        pt={2}
        sx={{ position: "relative" }}
      >
        <BackButton
          component={Link}
          to={`/quest_editor/promotion/${promotion.id}`}
          sx={{ position: "absolute", left: 0 }}
        >
          {t("promotion_duplication.button.back")}
        </BackButton>
        <ActionButton
          onClick={handleDuplicate}
          disabled={!newPromotionName.trim() || isSubmitting}
        >
          {t("promotion_duplication.button.duplicate")}
        </ActionButton>
      </Stack>
    </Stack>
  );
};

export default PromotionDuplication;
